import type { Address, Coordinate, RegionDTO, VenueValues } from '../types';
import { VenueType } from '../types';
import { COUNTRIES_REQUIRING_REGION } from './config';

type AddressComponentKeyType =
    | 'country'
    | 'route'
    | 'street_number'
    | 'subpremise'
    | 'locality'
    | 'sublocality'
    | 'neighborhood'
    | 'administrative_area_level_1'
    | 'administrative_area_level_2'
    | 'administrative_area_level_3'
    | 'postal_town'
    | 'postal_code'
    | 'establishment';
type ComponentNameType = 'short_name' | 'long_name';
type AddressComponent = Record<AddressComponentKeyType, string>;
type AddressComponentMap = Record<string, ComponentNameType>;

const ADDRESS_COMPONENT_PROPS: AddressComponentMap = {
    country: 'short_name',
    route: 'long_name',
    street_number: 'long_name',
    subpremise: 'long_name',
    locality: 'long_name',
    sublocality: 'long_name',
    administrative_area_level_1: 'short_name',
    administrative_area_level_2: 'short_name',
    administrative_area_level_3: 'short_name',
    postal_town: 'long_name',
    postal_code: 'long_name',
    establishment: 'long_name',
};

export const verifyVenueRegion = ({
    country,
    region,
    regions = [],
}: {
    country: string;
    region: string;
    regions: RegionDTO[];
}): boolean => {
    if (!country) {
        return false;
    }

    const countryRequiresRegion = COUNTRIES_REQUIRING_REGION.includes(country);

    // if there is no region, the only remaining check is whether they
    // were supposed to enter something or not.
    //
    // example positive:
    //    French address no region.
    //    France does not require region, so region is valid.
    // example negative:
    //    Irish address no region.
    //    Ireland requires region, so region is invalid.
    if (!region) {
        return !countryRequiresRegion;
    }

    const countryRegionList = regions.filter((r) => r.country_code === country);

    // if this country doesn't have a list and there is a region, it's valid
    //
    // example positive:
    //    French address with region (for some reason).
    //    France has no list against which to validate, so region is valid.
    // example negative:
    //    Irish address with region.
    //    Ireland has region list, don't return early/proceed to next check.
    if (!countryRegionList || !countryRegionList.length) {
        return true;
    }

    // at this point, there is user input and there is a list of regions
    // for their chosen country.  just make sure it's in the accepted list.
    //
    // example positive:
    //    Irish address with region from list, region is valid.
    // example negative:
    //    Irish address with region not from list, region is invalid.

    const foundRegion = countryRegionList.find(
        (r) => r.label === region || r.code === region,
    );
    return !!foundRegion;
};

export const formatAddressComponents = (
    addressComponents?: google.maps.GeocoderAddressComponent[],
) => {
    if (!addressComponents) {
        return {};
    }

    return addressComponents.reduce(
        (
            addressMap: Partial<AddressComponent>,
            addressComponent: google.maps.GeocoderAddressComponent,
        ) => {
            const addressMapWithComponent = addressMap;
            const componentType = addressComponent.types.find(
                (addressType: string) => ADDRESS_COMPONENT_PROPS[addressType],
            );

            if (componentType) {
                const key = ADDRESS_COMPONENT_PROPS[componentType];
                addressMapWithComponent[
                    componentType as AddressComponentKeyType
                ] = addressComponent[key];
            }

            return addressMapWithComponent;
        },
        {},
    );
};

export const arrangeStreetNumberByCountry = (
    component: Partial<AddressComponent>,
) => {
    const { country, route, street_number: streetNumber } = component;
    return country === 'DE'
        ? `${route} ${streetNumber}`
        : `${streetNumber} ${route}`;
};

export const getAddress1 = (component: Partial<AddressComponent>) => {
    const { route, street_number: streetNumber } = component;
    const address = streetNumber
        ? arrangeStreetNumberByCountry(component)
        : route || '';
    return address.trim();
};

export const getAddress2 = (component: Partial<AddressComponent>) =>
    component.subpremise ? `#${component.subpremise}` : '';

export const getCity = (component: Partial<AddressComponent>) => {
    const {
        locality,
        sublocality,
        administrative_area_level_3: region3,
        postal_town: postalTown,
    } = component;

    return locality || sublocality || region3 || postalTown || '';
};

export const getRegion = ({
    component,
}: {
    component: Partial<AddressComponent>;
}) => {
    const {
        administrative_area_level_1: region1,
        administrative_area_level_2: region2,
        administrative_area_level_3: region3,
    } = component;

    return region1 || region2 || region3 || '';
};

export const getCoordinates = (
    place: google.maps.places.PlaceResult,
): Coordinate => {
    let coords: Coordinate = {
        latitude: '',
        longitude: '',
    };

    if (place && place.geometry && place.geometry.location) {
        const { lat, lng } = place.geometry.location.toJSON();

        coords = {
            latitude: `${lat || ''}`,
            longitude: `${lng || ''}`,
        };
    }

    return coords;
};

export const parseAddressFromGooglePlace = (
    place: google.maps.places.PlaceResult,
) => {
    const addressComponents = formatAddressComponents(
        place['address_components'],
    );

    const { latitude, longitude } = getCoordinates(place);
    const address1 = getAddress1(addressComponents);
    const address2 = getAddress2(addressComponents);
    const city = getCity(addressComponents);
    const region = getRegion({
        component: addressComponents,
    });

    const postalCode = addressComponents['postal_code'] || '';
    const getRegionByKey = (
        region: string | RegionDTO,
        key: 'label' | 'code',
    ) => {
        if (typeof region === 'string') {
            return region;
        }

        return region[key];
    };

    const localizedAddress =
        place['formatted_address'] ||
        [
            address1,
            address2,
            city,
            `${getRegionByKey(region, 'label')} ${postalCode}`,
        ]
            .filter(Boolean)
            .join(', ')
            .trim();

    return {
        country: addressComponents.country || '',
        address1,
        address2,
        city,
        region: getRegionByKey(region, 'code'),
        postalCode,
        latitude,
        longitude,
        localizedAddress,
    };
};

function normalizeAddress(address: Address) {
    return {
        ...address,
        country: address.country || '',
        region: address.region || '',
    };
}

export const getLocalizedAddress = (address: Address) => {
    const { address1, address2, city, region, postalCode } = address;

    // Combine region and postal code, only if at least one exists
    const regionPostal = [region, postalCode].filter(Boolean).join(' ').trim();

    const localizedAddress = [address1, address2, city, regionPostal]
        .filter(Boolean)
        .join(', ')
        .trim();

    return localizedAddress;
};

export const createVenueFromGooglePlace = (
    place: google.maps.places.PlaceResult,
): VenueValues => {
    const address = parseAddressFromGooglePlace(place);

    return {
        id: place.place_id || '',
        name: place.name || '',
        address: normalizeAddress(address),
        latitude: address.latitude,
        longitude: address.longitude,
        type: VenueType.Google,
    };
};

export const createVenueFromOrgVenue = (venue: VenueValues): VenueValues => {
    return {
        ...venue,
        address: normalizeAddress(venue.address),
    };
};

type AddressKey = keyof Address;
const ADDRESS_STR_FIELDS: AddressKey[] = [
    'address1',
    'address2',
    'city',
    'region',
    'postalCode',
    'country',
];

/**
 * Countries that use postal codes with spaces that Google Geocoding API
 * may not handle correctly. For these countries, we remove spaces from
 * the postal code before sending to the geocoding API.
 *
 * UK postal codes: "PR1 2PP" → "PR12PP"
 * Canadian postal codes: "M5V 3A4" → "M5V3A4"
 */
const COUNTRIES_WITH_SPACED_POSTAL_CODES = ['GB', 'UK', 'CA'];

/**
 * Normalizes a postal code for geocoding by removing spaces for countries
 * where Google's Geocoding API may not handle spaced postal codes correctly.
 *
 * This fixes an issue where UK postal codes like "PR1 2PP" would be interpreted
 * as just "PR1", resulting in incorrect geocoding coordinates.
 *
 * @param postalCode - The postal code to normalize
 * @param country - The country code (e.g., 'GB', 'US')
 * @returns The normalized postal code (spaces removed for affected countries)
 */
export const normalizePostalCodeForGeocoding = (
    postalCode: string,
    country: string,
): string => {
    if (!postalCode) {
        return '';
    }

    const trimmedPostal = postalCode.trim();

    // Remove spaces from postal codes in countries where Google's
    // geocoding API may not handle spaced postal codes correctly
    if (COUNTRIES_WITH_SPACED_POSTAL_CODES.includes(country.toUpperCase())) {
        return trimmedPostal.replace(/\s+/g, '');
    }

    return trimmedPostal;
};

/**
 * Given our object representation of an address, make an address string suitable
 * for the geocode api and other methods that take address string inputs.
 *
 * This function normalizes postal codes for countries like UK where
 * spaces in postal codes can cause incorrect geocoding results.
 *
 * @param venue {VenueValues} - The venue object to extract the address from
 */
export const makeAddressStr = (venue: VenueValues) => {
    const { address } = venue;
    const country = (address.country || '').trim();

    return ADDRESS_STR_FIELDS.map((fieldName) => {
        const value = (address[fieldName] || '').trim();

        // Normalize postal code for geocoding to fix UK/CA postal code issues
        if (fieldName === 'postalCode' && value) {
            return normalizePostalCodeForGeocoding(value, country);
        }

        return value;
    })
        .filter((val) => val)
        .join(', ');
};
